/**
 * Mobile menu drawer - open/close, drill-down submenus (horizontal steps), scroll lock, focus.
 * Submenus slide like cart-drawer steps; Back / Escape pop one level; close resets DOM.
 * Close uses one fixed delay matching CSS transition (no transitionend + fallback race).
 * Swipe-to-close (touch, ≤767px) mirrors cart-drawer initSwipeToClose: drag left, release to commit.
 * Policy / contact bottom sheets: swipe down on handle or header (policy scroll when scrollTop=0) to dismiss.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'mm-open';
  /* Match the 240ms exit motion + a small Safari transitionend buffer. */
  var CLOSE_MS = 270;

  var sel = {
    wrapper: '[data-mm-wrapper]',
    drawer: '.mm-drawer',
    backdrop: '.mm-backdrop',
    openBtn: '[data-mm-open]',
    closeBtn: '[data-mm-close]',
    toggle: '[data-mm-toggle]',
    back: '[data-mm-back]',
    menuItem: '.mm-item',
    submenu: '.mm-sub',
    link: 'a.mm-link',
    focusable:
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  };

  var closeTimerId = null;
  var focusedElementBeforeOpen = null;

  function drawerIsOpen() {
    return document.documentElement.classList.contains(OPEN_CLASS);
  }

  var wrapper = null;
  var drawer = null;

  /** Background scroll lock via document-level touchmove/wheel listeners.
      Avoids position:fixed / overflow:hidden on body, which make iOS
      Safari reset scroll position and cause the background to jump. */
  var mmBackgroundScrollLockId = null;
  function applyMmBackgroundScrollLock() {
    if (mmBackgroundScrollLockId !== null || !window.themeSheetScrollLock) return;
    mmBackgroundScrollLockId = window.themeSheetScrollLock.lock(function () {
      return document.querySelector('html.mm-open .mm-drawer');
    });
  }
  function releaseMmBackgroundScrollLock() {
    if (mmBackgroundScrollLockId === null || !window.themeSheetScrollLock) return;
    window.themeSheetScrollLock.unlock(mmBackgroundScrollLockId);
    mmBackgroundScrollLockId = null;
  }

  /** Drill-down panels: horizontal track of slides; stack holds moved .mm-sub nodes for restore. */
  var drill = {
    initialized: false,
    viewport: null,
    track: null,
    stack: [],
    slideWidth: 0,
    titleEl: null,
    backBtn: null,
    headerEl: null,
    rootTitle: ''
  };

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** How many .mm-sub ancestors (including self) under drawer - for bottom-up height sync. */
  function mmSubmenuDepth(sub) {
    var n = 0;
    var el = sub;
    while (el && el !== drawer) {
      if (el.classList && el.classList.contains('mm-sub')) n += 1;
      el = el.parentElement;
    }
    return n;
  }

  /**
   * Accordion: set each ul.mm-sub max-height from measured scrollHeight (not 2000px).
   * Must process deepest submenus first so parent scrollHeight includes fully laid-out nested panels.
   */
  function syncSubmenuHeights() {
    if (!drawer) return;
    /* Root slide only uses horizontal drill; deeper levels use accordion - need height sync. */
    if (drawer.classList.contains('mm-drill-mode') && drill.stack.length === 0) return;

    var subs = Array.prototype.slice.call(drawer.querySelectorAll('.mm-sub'));
    subs.sort(function (a, b) {
      return mmSubmenuDepth(b) - mmSubmenuDepth(a);
    });

    if (prefersReducedMotion()) {
      subs.forEach(function (sub) {
        var item = sub.parentElement;
        if (!item || !item.classList.contains('mm-item')) return;
        if (item.getAttribute('data-open') === 'true') {
          sub.style.maxHeight = 'none';
        } else {
          sub.style.maxHeight = '';
        }
      });
      return;
    }

    subs.forEach(function (sub) {
      var item = sub.parentElement;
      if (!item || !item.classList.contains('mm-item')) return;
      if (item.getAttribute('data-open') === 'true') {
        sub.style.maxHeight = sub.scrollHeight + 'px';
      } else {
        sub.style.maxHeight = '0px';
      }
    });
  }

  /** Wait layout, then sync twice so nested depths settle (L3 under L2 under L1). */
  function scheduleSyncSubmenuHeights() {
    if (!drawer) return;
    if (drawer.classList.contains('mm-drill-mode') && drill.stack.length === 0) return;
    if (prefersReducedMotion()) {
      syncSubmenuHeights();
      return;
    }
    requestAnimationFrame(function () {
      syncSubmenuHeights();
      requestAnimationFrame(function () {
        syncSubmenuHeights();
        requestAnimationFrame(function () {
          syncSubmenuHeights();
        });
      });
    });
  }

  /** True on the first (root) slide only - toggles drill horizontally; deeper slides use accordion. */
  function drillIsRootSlide() {
    return !drill.initialized || drill.stack.length === 0;
  }

  /** Collapse accordion state under a submenu tree before it is moved back (e.g. Back). */
  function drillResetAccordionInSubtree(sub) {
    if (!sub) return;
    sub.querySelectorAll('.mm-item[data-open="true"]').forEach(function (item) {
      item.setAttribute('data-open', 'false');
      var t = item.querySelector(':scope > .mm-toggle');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
    sub.querySelectorAll('.mm-sub').forEach(function (s) {
      s.style.maxHeight = '';
    });
  }

  function drillToggleButtonForLi(li) {
    return li ? li.querySelector(':scope > .mm-toggle') : null;
  }

  function drillToggleTitle(button) {
    if (!button) return '';
    var span = button.querySelector('.mm-text');
    return (span && span.textContent) || button.textContent || '';
  }

  function drillSetTranslate() {
    if (!drill.track) return;
    var depth = drill.stack.length;
    var direction = document.documentElement.dir === 'rtl' ? 1 : -1;
    var dx = direction * depth * drill.slideWidth;
    drill.track.style.transform = 'translateX(' + dx + 'px)';
  }

  function drillUpdateHeader() {
    if (!drill.titleEl || !drill.backBtn || !drill.headerEl) return;
    var depth = drill.stack.length;
    if (drawer) drawer.classList.toggle('mm-drill-has-depth', depth > 0);
    if (depth === 0) {
      drill.titleEl.textContent = drill.rootTitle;
      drill.backBtn.hidden = true;
      drill.headerEl.classList.remove('mm-header--has-back');
    } else {
      var top = drill.stack[depth - 1];
      drill.titleEl.textContent = top.title || '';
      drill.backBtn.hidden = false;
      drill.headerEl.classList.add('mm-header--has-back');
    }
  }

  function drillUpdateLayout() {
    if (!drill.initialized || !drill.viewport || !drill.track) return;
    var w = drill.viewport.offsetWidth || 0;
    if (w < 1) return;
    drill.slideWidth = w;
    drill.viewport.style.setProperty('--mm-drill-w', w + 'px');
    var slides = drill.track.querySelectorAll('.mm-drill-slide');
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      slide.style.flexBasis = w + 'px';
      slide.style.width = w + 'px';
      slide.style.minWidth = w + 'px';
    }
    drillSetTranslate();
  }

  function drillPush(button) {
    if (!drill.initialized || !button) return false;
    var menuItem = button.closest(sel.menuItem);
    if (!menuItem) return false;
    var sub = menuItem.querySelector(':scope > .mm-sub');
    if (!sub) return false;

    var title = drillToggleTitle(button);
    var slide = document.createElement('div');
    slide.className = 'mm-drill-slide';
    slide.appendChild(sub);
    drill.track.appendChild(slide);

    drill.stack.push({ parentLi: menuItem, sub: sub, title: title });
    button.setAttribute('aria-expanded', 'true');

    /* Expose the primary task group immediately on a category slide. This keeps
       the drill-down useful at a glance while secondary groups remain compact. */
    var firstGroupToggle = sub.querySelector(':scope > .mm-item > .mm-toggle');
    if (firstGroupToggle) {
      var firstGroupItem = firstGroupToggle.closest(sel.menuItem);
      firstGroupItem.setAttribute('data-open', 'true');
      firstGroupToggle.setAttribute('aria-expanded', 'true');
    }

    drillUpdateLayout();
    drillUpdateHeader();
    scheduleSyncSubmenuHeights();

    if (!prefersReducedMotion()) {
      requestAnimationFrame(function () {
        var slide = drill.track && drill.track.lastElementChild;
        if (!slide) return;
        var f = slide.querySelector(sel.focusable);
        if (f) {
          try {
            f.focus({ preventScroll: true });
          } catch (e) {}
        }
      });
    }
    return true;
  }

  function drillPop() {
    if (!drill.initialized || drill.stack.length === 0) return;
    var entry = drill.stack.pop();
    var btn = drillToggleButtonForLi(entry.parentLi);
    if (btn) btn.setAttribute('aria-expanded', 'false');

    drillResetAccordionInSubtree(entry.sub);

    entry.parentLi.appendChild(entry.sub);

    var last = drill.track.lastElementChild;
    if (last && last.classList.contains('mm-drill-slide')) {
      last.remove();
    }

    drillUpdateLayout();
    drillUpdateHeader();

    if (btn && !prefersReducedMotion()) {
      requestAnimationFrame(function () {
        try {
          btn.focus({ preventScroll: true });
        } catch (e) {}
      });
    }
  }

  function drillReset() {
    if (!drill.initialized || !drill.track) return;
    while (drill.stack.length > 0) {
      var entry = drill.stack.pop();
      var btn = drillToggleButtonForLi(entry.parentLi);
      if (btn) btn.setAttribute('aria-expanded', 'false');
      drillResetAccordionInSubtree(entry.sub);
      entry.parentLi.appendChild(entry.sub);
      var last = drill.track.lastElementChild;
      if (last && last.classList.contains('mm-drill-slide')) {
        last.remove();
      }
    }
    drillSetTranslate();
    drillUpdateHeader();
    drillUpdateLayout();
  }

  function initDrill() {
    if (!drawer || drill.initialized) return;
    if (drawer.hasAttribute('data-mm-drill-initialized')) return;

    var menu = drawer.querySelector('.mm-nav .mm-menu');
    if (!menu) return;

    drill.rootTitle = drawer.dataset.mmRootTitle || drawer.getAttribute('data-mm-root-title') || 'Menu';
    drill.titleEl = drawer.querySelector('.mm-title');
    drill.backBtn = drawer.querySelector(sel.back);
    drill.headerEl = drawer.querySelector('.mm-header');

    var wrap = document.createElement('div');
    wrap.className = 'mm-drill';
    var viewport = document.createElement('div');
    viewport.className = 'mm-drill-viewport';
    var track = document.createElement('div');
    track.className = 'mm-drill-track';
    var slide0 = document.createElement('div');
    slide0.className = 'mm-drill-slide';

    menu.parentNode.insertBefore(wrap, menu);
    slide0.appendChild(menu);
    var rootHighlights = wrap.nextElementSibling;
    if (rootHighlights && rootHighlights.classList.contains('mm-root-highlights')) {
      var footer = drawer.querySelector(':scope > .mm-footer');
      if (footer) drawer.insertBefore(rootHighlights, footer);
    }
    track.appendChild(slide0);
    viewport.appendChild(track);
    wrap.appendChild(viewport);

    drill.viewport = viewport;
    drill.track = track;
    drill.initialized = true;
    drawer.classList.add('mm-drill-mode');
    drawer.setAttribute('data-mm-drill-initialized', 'true');

    drillUpdateLayout();
  }

  function syncScrollbarGutter() {
    var w = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--mm-scrollbar-gutter', w > 0 ? w + 'px' : '0px');
  }

  function clearScrollbarGutter() {
    document.documentElement.style.removeProperty('--mm-scrollbar-gutter');
  }

  function finishMenuClose() {
    document.documentElement.classList.remove(OPEN_CLASS);
    document.documentElement.classList.remove('mm-closing');
    if (drawer) drawer.classList.remove('mm-closing');
    if (wrapper) wrapper.setAttribute('aria-hidden', 'true');
    if (drawer) drawer.setAttribute('aria-modal', 'false');

    clearScrollbarGutter();
    releaseMmBackgroundScrollLock();

    if (focusedElementBeforeOpen && focusedElementBeforeOpen.focus) {
      try {
        focusedElementBeforeOpen.focus();
      } catch (e) {}
    }

    if (drawer) {
      drillReset();
      drawer.querySelectorAll('.mm-item[data-open="true"]').forEach(function (item) {
        item.setAttribute('data-open', 'false');
        var t = item.querySelector(':scope > .mm-toggle');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      drawer.querySelectorAll('.mm-sub').forEach(function (sub) {
        sub.style.maxHeight = '';
      });
    }

    document.dispatchEvent(new CustomEvent('mm:closed'));
  }

  function openMenu() {
    if (document.documentElement.classList.contains('mm-closing')) return;
    if (drawerIsOpen()) return;

    document.documentElement.classList.remove('mm-closing');
    if (drawer) {
      drawer.classList.remove('mm-closing', 'mm-swiping');
      drawer.style.transition = '';
      drawer.style.transform = '';
      drawer.style.opacity = '';
    }

    focusedElementBeforeOpen = document.activeElement;

    syncScrollbarGutter();
    applyMmBackgroundScrollLock();
    document.documentElement.classList.add(OPEN_CLASS);
    wrapper.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('aria-modal', 'true');

    function applyInitialFocus() {
      if (!drawerIsOpen()) return;
      /* Skip flow close pill - it is 5×320px; focusing it looks like a giant selection ring.
         Focus first real control (menu link/toggle, footer, etc.). */
      var candidates = drawer.querySelectorAll(sel.focusable);
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (el.matches('[data-mm-close]')) continue;
        el.focus({ preventScroll: true });
        return;
      }
    }
    if (prefersReducedMotion()) {
      applyInitialFocus();
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(applyInitialFocus);
      });
    }

    document.dispatchEvent(new CustomEvent('mm:opened'));

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (drawer && drawer.classList.contains('mm-drill-mode')) drillUpdateLayout();
      });
    });
  }

  /** Close policy sheets + contact bottom sheet inside the drawer footer (keep body scroll lock until finishMenuClose). */
  function closeMmDrawerFooterSheets() {
    document.querySelectorAll('.mm-policy-sheet.is-open').forEach(function (el) {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      var c = el.querySelector('.mm-policy-sheet__content');
      if (c) {
        c.style.transition = '';
        c.style.transform = '';
      }
    });
    var popup = document.querySelector('.mm-contact-popup.is-open');
    if (popup) {
      popup.classList.remove('is-open');
      var cc = popup.querySelector('.mm-contact-popup__content');
      if (cc) {
        cc.style.transition = '';
        cc.style.transform = '';
      }
    }
  }

  function closeMenu() {
    if (!drawerIsOpen()) return;
    if (document.documentElement.classList.contains('mm-closing')) return;

    closeMmDrawerFooterSheets();

    if (drawer) {
      drawer.classList.remove('mm-swiping');
      drawer.style.transition = '';
      drawer.style.transform = '';
      drawer.style.opacity = '';
    }

    document.documentElement.classList.add('mm-closing');
    if (drawer) drawer.classList.add('mm-closing');

    if (prefersReducedMotion()) {
      finishMenuClose();
      return;
    }

    clearTimeout(closeTimerId);
    closeTimerId = setTimeout(function () {
      closeTimerId = null;
      finishMenuClose();
    }, CLOSE_MS);
  }

  function toggleMenu() {
    drawerIsOpen() ? closeMenu() : openMenu();
  }

  /** Close every open descendant .mm-item (deepest accordion state). */
  function mmCloseNestedAccordions(rootItem) {
    rootItem.querySelectorAll('.mm-item[data-open="true"]').forEach(function (nested) {
      nested.setAttribute('data-open', 'false');
      var nt = nested.querySelector(':scope > .mm-toggle');
      if (nt) nt.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Accordion toggle: one open branch per sibling group; closing a branch collapses descendants.
   * Heights are synced after DOM state updates (deepest-first in syncSubmenuHeights).
   */
  function toggleAccordion(button) {
    var menuItem = button.closest(sel.menuItem);
    if (!menuItem) return;

    var expanded = menuItem.getAttribute('data-open') === 'true';
    var parentList = menuItem.parentElement;

    if (parentList) {
      Array.from(parentList.children).forEach(function (sibling) {
        if (sibling === menuItem || !sibling.classList || !sibling.classList.contains('mm-item')) return;
        if (sibling.getAttribute('data-open') !== 'true') return;
        sibling.setAttribute('data-open', 'false');
        var st = sibling.querySelector(':scope > .mm-toggle');
        if (st) st.setAttribute('aria-expanded', 'false');
        mmCloseNestedAccordions(sibling);
      });
    }

    var nextOpen = expanded ? 'false' : 'true';
    menuItem.setAttribute('data-open', nextOpen);
    button.setAttribute('aria-expanded', nextOpen);

    if (expanded) {
      mmCloseNestedAccordions(menuItem);
    }

    scheduleSyncSubmenuHeights();
  }

  function trapFocus(event) {
    if (!drawerIsOpen() || event.key !== 'Tab') return;
    var nodes = drawer.querySelectorAll(sel.focusable);
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && drawerIsOpen()) {
      event.preventDefault();
      if (drill.initialized && drill.stack.length > 0) {
        drillPop();
        return;
      }
      closeMenu();
    }
    trapFocus(event);
  }

  function handleClick(event) {
    var raw = event.target;
    var target = raw && raw.nodeType === Node.TEXT_NODE ? raw.parentElement : raw;
    if (!target || typeof target.closest !== 'function') return;

    var openBtn = target.closest(sel.openBtn);
    if (openBtn) {
      event.preventDefault();
      openMenu();
      event.stopImmediatePropagation();
      return;
    }

    var cbtn = target.closest(sel.closeBtn);
    if (cbtn && drawer.contains(cbtn)) {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (target.classList && target.classList.contains('mm-backdrop') && drawerIsOpen()) {
      closeMenu();
      return;
    }

    var backBtn = target.closest(sel.back);
    if (backBtn && drawer.contains(backBtn)) {
      event.preventDefault();
      if (drill.initialized && drill.stack.length > 0) drillPop();
      return;
    }

    var toggleBtn = target.closest(sel.toggle);
    if (toggleBtn && drawer.contains(toggleBtn)) {
      event.preventDefault();
      if (drill.initialized && drillIsRootSlide()) {
        if (drillPush(toggleBtn)) return;
      } else if (drill.initialized) {
        toggleAccordion(toggleBtn);
        return;
      }
      toggleAccordion(toggleBtn);
      return;
    }

    var navLink = target.closest(sel.link);
    if (navLink && drawer.contains(navLink) && drawerIsOpen()) {
      closeMenu();
    }
  }

  /**
   * Swipe toward the drawer's opening edge to close. LTR drawers close left;
   * RTL drawers close right so the physical gesture follows the mirrored UI.
   */
  function initSwipeToClose() {
    if (!drawer || !wrapper) return;
    if (drawer.hasAttribute('data-mm-swipe-close-initialized')) return;
    drawer.setAttribute('data-mm-swipe-close-initialized', 'true');

    var OUT = 'cubic-bezier(0.32, 0.72, 0, 1)';
    var BACK = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    var D_OUT = 320;
    var D_BACK = 400;

    var st = {
      x0: 0,
      y0: 0,
      x: 0,
      y: 0,
      t0: 0,
      on: false,
      moved: false,
      started: false
    };

    function dragMax() {
      return Math.min(window.innerWidth * 0.45, 220);
    }

    function clearInline(el) {
      if (!el) return;
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
      el.classList.remove('mm-swiping');
    }

    function dragEl(el, dx) {
      var m = dragMax();
      var px = document.documentElement.dir === 'rtl'
        ? Math.min(dx * 0.35, m)
        : Math.max(dx * 0.35, -m);
      el.style.transition = 'none';
      el.style.transform = 'translateX(' + px + 'px)';
      el.style.opacity = String(Math.max(0.92, 1 - Math.abs(px) / (m * 2.2)));
    }

    function settle(el) {
      if (!el) return;
      el.classList.remove('mm-swiping');
      el.style.transition = 'transform ' + D_BACK + 'ms ' + BACK + ', opacity ' + D_BACK + 'ms ' + BACK;
      el.style.transform = '';
      el.style.opacity = '';
      setTimeout(function () {
        clearInline(el);
      }, D_BACK);
    }

    function slideOutClose(el) {
      if (!el) return;
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onTr);
        clearTimeout(fb);
        clearInline(el);
        closeMmDrawerFooterSheets();
        finishMenuClose();
      }
      el.classList.remove('mm-swiping');
      el.style.transition = 'transform ' + D_OUT + 'ms ' + OUT + ', opacity ' + D_OUT + 'ms ' + OUT;
      el.style.transform = document.documentElement.dir === 'rtl'
        ? 'translateX(102%)'
        : 'translateX(-102%)';
      el.style.opacity = '0';
      function onTr(ev) {
        if (ev.propertyName !== 'transform') return;
        finish();
      }
      el.addEventListener('transitionend', onTr);
      var fb = setTimeout(finish, D_OUT + 100);
    }

    function canStart(e) {
      if (!drawerIsOpen() || window.innerWidth > 767) return false;
      if (prefersReducedMotion()) return false;
      if (!drawer.contains(e.target)) return false;
      var ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return false;
      if (e.target.closest('.mm-contact-popup.is-open')) return false;
      if (e.target.closest('.mm-policy-sheet.is-open')) return false;

      var p = e.touches[0];
      var rect = drawer.getBoundingClientRect();
      var edgeDistance = document.documentElement.dir === 'rtl'
        ? rect.right - p.clientX
        : p.clientX - rect.left;
      if (edgeDistance <= 40) return true;

      if (e.target.closest('.mm-header')) return true;

      var block = e.target.closest(
        'a[href], button, input, textarea, select, [role="button"], [data-contact-toggle], [data-contact-close], .mm-contact-option'
      );
      if (block && drawer.contains(block)) return false;

      /* Footer is outside .mm-body - still allow swipe-to-close on non-interactive areas */
      return !!(e.target.closest('.mm-body') || e.target.closest('.mm-footer'));
    }

    function reset() {
      st.x0 = st.y0 = st.x = st.y = st.t0 = 0;
      st.on = st.moved = false;
      st.started = false;
    }

    function onStart(e) {
      if (!canStart(e)) return;
      if (!e.touches || e.touches.length === 0) return;
      var p = e.touches[0];
      st.x0 = st.x = p.clientX;
      st.y0 = st.y = p.clientY;
      st.t0 = Date.now();
      st.on = true;
      st.moved = false;
      st.started = true;
    }

    function onMove(e) {
      if (!st.on || !drawerIsOpen()) return;
      if (!e.touches || e.touches.length === 0) return;
      var p = e.touches[0];
      st.x = p.clientX;
      st.y = p.clientY;
      var dx = st.x - st.x0;
      var dy = Math.abs(st.y - st.y0);
      var isClosingDirection = document.documentElement.dir === 'rtl' ? dx > 0 : dx < 0;
      if (Math.abs(dx) > 8) st.moved = true;
      if (dy > 60 && Math.abs(dx) < 25) {
        st.on = false;
        return;
      }
      if (!isClosingDirection) {
        clearInline(drawer);
        return;
      }
      if (isClosingDirection && st.moved) {
        drawer.classList.add('mm-swiping');
        dragEl(drawer, dx);
        // Lock vertical scroll while actively dragging horizontally.
        if (e.cancelable) e.preventDefault();
      } else {
        clearInline(drawer);
      }
    }

    function onEnd() {
      if (!st.started) return;

      if (!st.on) {
        settle(drawer);
        reset();
        return;
      }
      st.on = false;
      var dx = st.x - st.x0;
      var dt = Math.max(Date.now() - st.t0, 1);
      var v = Math.abs(dx) / dt;
      var isClosingDirection = document.documentElement.dir === 'rtl' ? dx > 0 : dx < 0;
      var commit = isClosingDirection && st.moved && (Math.abs(dx) > 30 || v > 0.25);
      if (commit) slideOutClose(drawer);
      else settle(drawer);
      reset();
    }

    wrapper.addEventListener('touchstart', onStart, { passive: true });
    // onMove may preventDefault() during active horizontal drag - must be non-passive.
    wrapper.addEventListener('touchmove', onMove, { passive: false });
    wrapper.addEventListener('touchend', onEnd, { passive: true });
    wrapper.addEventListener('touchcancel', onEnd, { passive: true });
  }

  function init() {
    wrapper = document.querySelector(sel.wrapper);
    if (!wrapper) return;
    drawer = wrapper.querySelector(sel.drawer);
    if (!drawer) return;

    if (wrapper.parentElement !== document.body) {
      document.body.appendChild(wrapper);
    }
    wrapper.setAttribute('aria-hidden', 'true');

    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeydown);

    var resizeT = null;
    window.addEventListener('resize', function () {
      if (window.innerWidth >= 768 && drawerIsOpen()) {
        closeMenu();
        return;
      }
      if (!drawerIsOpen()) return;
      clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        resizeT = null;
        if (drawer && drawer.classList.contains('mm-drill-mode')) drillUpdateLayout();
        else syncSubmenuHeights();
      }, 80);
    });

    initDrill();

    initSwipeToClose();

    window.MobileMenu = {
      open: openMenu,
      close: closeMenu,
      toggle: toggleMenu,
      isOpen: drawerIsOpen
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function initContactPopup() {
    var toggleBtns = document.querySelectorAll('[data-contact-toggle]');
    var popup = document.querySelector('[data-contact-popup]');
    var closeBtns = document.querySelectorAll('[data-contact-close]');
    if (!popup || !toggleBtns.length) return;

    var contactContent = popup.querySelector('.mm-contact-popup__content');
    var contactHandle = popup.querySelector('.mm-contact-popup__handle');
    var contactScroll = popup.querySelector('.mm-contact-popup__scroll');

    function openPopup() {
      popup.classList.add('is-open');
      if (contactContent) {
        contactContent.style.transition = '';
        contactContent.style.transform = '';
      }
    }
    function closePopup() {
      popup.classList.remove('is-open');
      // Only release body scroll if no policy sheet is now open (e.g. user
      // opened Contact Information sheet from this popup).
      mmUnlockBodyIfNoPolicySheet();
      if (contactContent) {
        contactContent.style.transition = '';
        contactContent.style.transform = '';
      }
    }

    toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openPopup();
      });
    });
    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', closePopup);
    });
    popup.querySelectorAll('.mm-contact-option').forEach(function (option) {
      option.addEventListener('click', function () {
        setTimeout(closePopup, 100);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
    });

    if (contactContent && (contactHandle || contactScroll)) {
      attachSwipeDownToClose(popup, contactContent, closePopup, {
        handle: contactHandle,
        scroll: contactScroll
      });
    }
  }

  function mmUnlockBodyIfNoPolicySheet() {
    /* No-op: background scroll is held by the mobile-menu drawer's
       document-level touchmove/wheel listeners - body overflow is
       never modified here, so there is nothing to release. */
  }

  /**
   * Swipe down on handle / top strip (and policy scroll when scrollTop === 0) to dismiss bottom sheets.
   * Uses non-passive touchmove so pull-to-dismiss can prevent scroll while dragging.
   */
  function attachSwipeDownToClose(rootEl, contentEl, closeFn, opts) {
    if (!rootEl || !contentEl || !opts) return;
    var handle = opts.handle || null;
    var scroll = opts.scroll || null;
    if (!handle && !scroll) return;
    if (rootEl.hasAttribute('data-mm-swipe-down')) return;
    rootEl.setAttribute('data-mm-swipe-down', 'true');

    var CLOSE_DY = 72;
    var VEL_MS = 0.45;
    var MAX_DY = 400;

    var st = {
      active: false,
      dragging: false,
      isHandle: false,
      fromScroll: false,
      x0: 0,
      y0: 0,
      t0: 0
    };

    function clearTransform() {
      contentEl.style.transition = '';
      contentEl.style.transform = '';
      contentEl.style.willChange = '';
    }

    function applyDy(dy) {
      var y = Math.min(Math.max(0, dy), MAX_DY);
      contentEl.style.transition = 'none';
      contentEl.style.transform = 'translateY(' + y + 'px)';
      contentEl.style.willChange = 'transform';
    }

    function snapBack() {
      contentEl.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
      contentEl.style.transform = 'translateY(0)';
      setTimeout(function () {
        clearTransform();
      }, 320);
    }

    function reset() {
      st.active = st.dragging = st.isHandle = st.fromScroll = false;
      st.x0 = st.y0 = st.t0 = 0;
    }

    function onTouchStart(e) {
      if (window.innerWidth > 767) return;
      if (prefersReducedMotion()) return;
      if (!rootEl.classList.contains('is-open')) return;
      if (!e.touches || e.touches.length === 0) return;

      var p = e.touches[0];
      st.x0 = p.clientX;
      st.y0 = p.clientY;
      st.t0 = Date.now();
      st.active = false;
      st.dragging = false;
      st.isHandle = !!(handle && handle.contains(e.target));
      st.fromScroll = !!(scroll && scroll.contains(e.target) && !st.isHandle);

      if (st.isHandle) {
        st.active = true;
        return;
      }
      if (st.fromScroll && scroll.scrollTop <= 0) {
        st.active = true;
        return;
      }
    }

    function onTouchMove(e) {
      if (!st.active) return;
      if (!e.touches || e.touches.length === 0) return;
      var p = e.touches[0];
      var dy = p.clientY - st.y0;
      var dx = p.clientX - st.x0;

      if (!st.dragging) {
        if (st.isHandle) {
          if (dy > 0 && Math.abs(dy) > Math.abs(dx) * 0.45) {
            st.dragging = true;
          } else if (Math.abs(dx) > 22) {
            st.active = false;
            reset();
            return;
          } else {
            return;
          }
        } else if (st.fromScroll) {
          if (scroll.scrollTop > 0) {
            st.active = false;
            reset();
            return;
          }
          if (dy > 10 && dy > Math.abs(dx) * 0.45) {
            st.dragging = true;
          } else {
            return;
          }
        } else {
          return;
        }
      }

      e.preventDefault();
      applyDy(dy);
    }

    function onTouchEnd(e) {
      if (!st.active) return;

      var dt = Math.max(Date.now() - st.t0, 1);
      if (st.dragging) {
        var dy = 0;
        var ch = e.changedTouches && e.changedTouches[0];
        if (ch) {
          dy = ch.clientY - st.y0;
        }
        var v = dy / dt;
        if (dy > CLOSE_DY || v > VEL_MS) {
          clearTransform();
          closeFn();
        } else {
          snapBack();
        }
      }
      reset();
    }

    rootEl.addEventListener('touchstart', onTouchStart, { passive: true });
    rootEl.addEventListener('touchmove', onTouchMove, { passive: false });
    rootEl.addEventListener('touchend', onTouchEnd, { passive: true });
    rootEl.addEventListener('touchcancel', onTouchEnd, { passive: true });
  }

  /**
   * Load policy HTML only when its mobile sheet is opened. The previous
   * server-rendered copies added every refund, shipping and contact document
   * to every storefront response, even though almost nobody opens them.
   */
  function ensurePolicySheetContent(sheetEl) {
    var body = sheetEl && sheetEl.querySelector('[data-mm-policy-lazy][data-policy-src]');
    if (!body || body.dataset.policyState === 'loaded' || body.dataset.policyState === 'loading') return;

    var sourceUrl = body.getAttribute('data-policy-src');
    if (!sourceUrl) return;
    body.dataset.policyState = 'loading';
    body.setAttribute('aria-busy', 'true');
    body.textContent = body.getAttribute('data-policy-loading') || 'Loading…';

    fetch(sourceUrl, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Policy request failed');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var selectors = (body.getAttribute('data-policy-selector') || '.shopify-policy__body').split(',');
        var source = null;
        selectors.some(function (selector) {
          source = doc.querySelector(selector.trim());
          return Boolean(source);
        });
        if (!source) throw new Error('Policy content was not found');

        var clone = source.cloneNode(true);
        body.replaceChildren();
        if (source.classList.contains('shopify-policy__body')) {
          while (clone.firstChild) body.appendChild(clone.firstChild);
        } else {
          body.appendChild(clone);
        }

        var idPrefix = body.getAttribute('data-policy-id-prefix') || 'mm-policy-';
        var idMap = {};
        body.querySelectorAll('[id]').forEach(function (element) {
          var oldId = element.id;
          var newId = idPrefix + oldId;
          idMap[oldId] = newId;
          element.id = newId;
        });
        body.querySelectorAll('[href^="#"], [for], [aria-controls], [aria-labelledby], [aria-describedby]').forEach(function (element) {
          var href = element.getAttribute('href');
          if (href && href.charAt(0) === '#' && idMap[href.slice(1)]) {
            element.setAttribute('href', '#' + idMap[href.slice(1)]);
          }
          ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby'].forEach(function (attribute) {
            var value = element.getAttribute(attribute);
            if (!value) return;
            element.setAttribute(attribute, value.split(/\s+/).map(function (id) {
              return idMap[id] || id;
            }).join(' '));
          });
        });

        if (body.getAttribute('data-policy-lazy-images') === 'true') {
          body.querySelectorAll('img').forEach(function (image) {
            image.loading = 'lazy';
            image.decoding = 'async';
          });
        }
        body.dataset.policyState = 'loaded';
      })
      .catch(function () {
        body.textContent = body.getAttribute('data-policy-error') || 'This policy could not be loaded.';
        body.dataset.policyState = 'error';
      })
      .finally(function () {
        body.removeAttribute('aria-busy');
      });
  }

  function initPolicySheet(openSelector, sheetEl, closeSelector) {
    var openBtns = document.querySelectorAll(openSelector);
    if (!sheetEl || !openBtns.length) return;

    var closeEls = sheetEl.querySelectorAll(closeSelector);
    var contentEl = sheetEl.querySelector('.mm-policy-sheet__content');
    var handleEl = sheetEl.querySelector('.mm-policy-sheet__handle');
    var scrollEl = sheetEl.querySelector('.mm-policy-sheet__scroll');

    function openSheet(e) {
      if (e) e.preventDefault();
      ensurePolicySheetContent(sheetEl);
      // Reset scroll to top every time the sheet opens
      if (scrollEl) scrollEl.scrollTop = 0;
      // Blur any lingering focus inside the sheet so previously-tapped
      // links don't appear pre-selected when the sheet reopens.
      if (sheetEl.contains(document.activeElement)) {
        try { document.activeElement.blur(); } catch (err) {}
      }
      sheetEl.classList.add('is-open');
      sheetEl.setAttribute('aria-hidden', 'false');
      if (contentEl) {
        contentEl.style.transition = '';
        contentEl.style.transform = '';
      }
    }
    function closeSheet() {
      sheetEl.classList.remove('is-open');
      sheetEl.setAttribute('aria-hidden', 'true');
      mmUnlockBodyIfNoPolicySheet();
      if (contentEl) {
        contentEl.style.transition = '';
        contentEl.style.transform = '';
      }
    }

    openBtns.forEach(function (btn) {
      btn.addEventListener('click', openSheet);
    });
    closeEls.forEach(function (el) {
      el.addEventListener('click', closeSheet);
    });

    if (contentEl && (handleEl || scrollEl)) {
      attachSwipeDownToClose(sheetEl, contentEl, closeSheet, {
        handle: handleEl,
        scroll: scrollEl
      });
    }
  }

  function initPolicySheets() {
    var shipping = document.querySelector('[data-mm-shipping-sheet]');
    var refund = document.querySelector('[data-mm-refund-sheet]');
    var contactInfo = document.querySelector('[data-mm-contact-info-sheet]');
    initPolicySheet('[data-mm-shipping-sheet-open]', shipping, '[data-mm-shipping-sheet-close]');
    initPolicySheet('[data-mm-refund-sheet-open]', refund, '[data-mm-refund-sheet-close]');
    initPolicySheet('[data-mm-contact-info-sheet-open]', contactInfo, '[data-mm-contact-info-sheet-close]');

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openSheets = document.querySelectorAll('.mm-policy-sheet.is-open');
      if (!openSheets.length) return;
      openSheets.forEach(function (s) {
        s.classList.remove('is-open');
        s.setAttribute('aria-hidden', 'true');
      });
      mmUnlockBodyIfNoPolicySheet();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactPopup);
    document.addEventListener('DOMContentLoaded', initPolicySheets);
  } else {
    initContactPopup();
    initPolicySheets();
  }
})();
